import { GreyBlackFieldProps, HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { UseFormReturn } from "react-hook-form";
import { useCallback, useState } from "react";
import DefaultErrorText from "components/Form/DefaultErrorText";
import { get } from "lodash";
import TemplateRoleSelect, { PrebuiltSelectRoleTaskAction } from "../Components/TemplateTaskAction/TemplateRoleSelect";
import TemplateInputOnboardingPlanTaskAction, { PrebuiltTemplateInputOnboardingPlanTaskAction } from "../Components/TemplateTaskAction/TemplateInputOnboardingPlan";
import TemplateInputEmailTaskAction, { PrebuiltTemplateInputEmailTaskAction } from "../Components/TemplateTaskAction/TemplateInputEmail";
import TemplateMessagePromptTaskAction, { PrebuiltTemplateMessagePromptTaskAction } from "../Components/TemplateTaskAction/TemplateMessagePrompt";
import TemplateSelectCustomFormTaskAction, { PrebuiltTemplateSelectCustomFormTaskAction } from "../Components/TemplateTaskAction/TemplateSelectCustomForm";
import { TaskTemplate } from "../models";
import AddTemplateTaskActionButton from "../Components/TemplateTaskAction/AddTemplateTaskActionButton";

interface TemplateTaskActionFieldProps extends Omit<GreyBlackFieldProps, "label"> {
  taskTemplate: TaskTemplate;
  form: UseFormReturn<TaskTemplate>;
  labelProps?: object;
}

interface RenderTemplateTaskActionProps {
  taskTemplate: TaskTemplate;
  form: UseFormReturn<TaskTemplate>;
}

export function RenderTemplateTaskAction({ taskTemplate, form }: RenderTemplateTaskActionProps) {
  const [autoOpen, setAutoOpen] = useState(false);
  const { watch, setValue } = form;
  const taskActionType = watch("taskAction");

  const onRemove = useCallback(() => {
    if (taskActionType === "send_message") {
      setValue("messageRecipient", null, { shouldDirty: true });
      setValue("messageBody", null, { shouldDirty: true });
    }
    if (taskActionType === "complete_form") {
      setValue("customFormId", null, { shouldDirty: true });
    }
    setValue("taskAction", null, { shouldDirty: true });
  }, [setValue, taskActionType]);

  switch (taskActionType) {
    case "assign_buddy":
      return <TemplateRoleSelect taskTemplate={taskTemplate} form={form} onRemove={onRemove} autoOpen={autoOpen} setAutoOpen={setAutoOpen} />;
    case "create_onboarding_plan":
      return <TemplateInputOnboardingPlanTaskAction taskTemplate={taskTemplate} onRemove={onRemove} autoOpen={autoOpen} setAutoOpen={setAutoOpen} />;
    case "create_email_account":
      return <TemplateInputEmailTaskAction taskTemplate={taskTemplate} onRemove={onRemove} autoOpen={autoOpen} setAutoOpen={setAutoOpen} />;
    case "send_message":
      return <TemplateMessagePromptTaskAction taskTemplate={taskTemplate} form={form} onRemove={onRemove} autoOpen={autoOpen} setAutoOpen={setAutoOpen} />;
    case "complete_form":
      return <TemplateSelectCustomFormTaskAction taskTemplate={taskTemplate} form={form} onRemove={onRemove} autoOpen={autoOpen} setAutoOpen={setAutoOpen} />;
    default:
      return (
        <AddTemplateTaskActionButton form={form} setAutoOpen={setAutoOpen} />
      );
  }
}

export function getCombinedMessageErrors(errors: any): string | null {
  const recipientError = get(errors, "messageRecipient.message");
  const bodyError = get(errors, "messageBody.message");

  const messages = [recipientError, bodyError].filter(Boolean);

  return messages.length > 0 ? "Message Recipient and Message Body are required for message prompts" : null;
}

export default function TemplateTaskActionField({
  taskTemplate,
  form,
  labelProps = {},
  ...props
}: TemplateTaskActionFieldProps) {
  const { formState: { errors } } = form;
  const error = getCombinedMessageErrors(errors);
  return (
    <>
      <HorizontalGreyBlackField labelProps={{ ...labelProps, mr: "8" }} {...props} label="Task Action">
        <RenderTemplateTaskAction taskTemplate={taskTemplate} form={form} />
      </HorizontalGreyBlackField>
      {error && <DefaultErrorText>{error}</DefaultErrorText>}
    </>
  );
}

interface PrebuiltTemplateTaskActionProps {
  prebuiltTaskTemplate: TaskTemplate;
}

function PrebuiltTemplateTaskAction({ prebuiltTaskTemplate }: PrebuiltTemplateTaskActionProps) {
  switch (prebuiltTaskTemplate?.taskAction) {
    case "assign_buddy":
      return <PrebuiltSelectRoleTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />;
    case "create_onboarding_plan":
      return <PrebuiltTemplateInputOnboardingPlanTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />;
    case "create_email_account":
      return <PrebuiltTemplateInputEmailTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />;
    case "send_message":
      return <PrebuiltTemplateMessagePromptTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />;
    case "complete_form":
      return <PrebuiltTemplateSelectCustomFormTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />;
    default:
      return "";
  }
}

interface ShowPrebuiltTemplateTaskActionProps {
  prebuiltTaskTemplate: TaskTemplate;
  labelProps?: object;
}

export function ShowPrebuiltTemplateTaskAction({
  prebuiltTaskTemplate,
  labelProps = {},
  ...props
}: ShowPrebuiltTemplateTaskActionProps) {
  return !prebuiltTaskTemplate.taskAction ? (
    null
  ) : (
    <HorizontalGreyBlackField labelProps={{ ...labelProps, mr: "8" }} {...props} label="Task Action">
      <PrebuiltTemplateTaskAction prebuiltTaskTemplate={prebuiltTaskTemplate} />
    </HorizontalGreyBlackField>
  );
}
